
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave, MdEdit } from 'react-icons/md';
import './AddEmployee.css'; // Reusing AddEmployee styles
import Swal from 'sweetalert2';
import { GET_ALL_ROLE_API, UPDATE_EMPLOYEE_API, VIEW_EMPLOYEE_API } from '../config/API';
import { authFetch } from '../components/utils/authFetch';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';

const EditEmployee = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Detect if we are in 'view' mode (from path or state)
    const [isViewMode, setIsViewMode] = useState(
        location.pathname.includes('/view/') || location.state?.mode === 'view'
    );

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        contactNumber: '',
        email: '',
        userName: '',
        gender: '',
        roleId: ''
    });

    const [roles, setRoles] = useState([]);

    const getAllRoles = async () => {
        try {
            const response = await fetch(GET_ALL_ROLE_API);
            const result = await response.json();

            if (result.status) {
                const filteredRoles = result.data.filter(
                    role => role.roleName !== "SUPER_ADMIN"
                );
                setRoles(filteredRoles);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchEmployeeById = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await authFetch(VIEW_EMPLOYEE_API(id), {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();

            if (result.status) {
                const emp = result.data;
                setFormData({
                    firstName: emp.firstName || '',
                    lastName: emp.lastName || '',
                    contactNumber: emp.contactNumber || '',
                    email: emp.email || '',
                    userName: emp.username || '',
                    gender: emp.gender || '',
                    roleId: emp.roleId || ''
                });
            } else {
                showErrorToast(result.message || 'Employee data not found');
                navigate('/employees');
            }
        } catch (error) {
            console.error('Error fetching employee:', error);
            showErrorToast('An error occurred while fetching employee data');
            navigate('/employees');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllRoles();
        if (id) {
            fetchEmployeeById();
        }
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isViewMode) return;

        const confirm = await Swal.fire({
            title: 'Update Employee?',
            text: 'Do you want to save the changes for this employee?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (!confirm.isConfirmed) return;

        try {
            setSaving(true);
            const token = localStorage.getItem('accessToken');

            const response = await authFetch(
                UPDATE_EMPLOYEE_API(id),
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(formData),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                showErrorToast(result.message || result.error || 'Failed to update employee');
                return;
            }

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Employee updated successfully',
                timer: 2000,
                showConfirmButton: false
            });
            navigate('/employees');

        } catch (error) {
            console.error('Error updating employee:', error);
            showErrorToast(error.message || 'An error occurred');
        } finally {
            setSaving(false);
        }
    };



    return (
        <div className="add-employee-page">
            <div className="page-header">
                <button onClick={() => navigate('/employees')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewMode ? 'Employee Details' : 'Edit Employee'}</h2>
                {isViewMode && (
                    <button onClick={() => setIsViewMode(false)} className="edit-toggle-btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        <MdEdit /> Edit Mode
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="employee-form">
                <div className="form-section">
                    <h3>Personal Details</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>First Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder="Enter first name"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Last Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                placeholder="Enter last name"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Contact Number <span className="required-star">*</span></label>
                            <input
                                type="tel"
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleChange}
                                placeholder="Enter contact number"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Email ID</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email address"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Gender <span className="required-star">*</span></label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                disabled={isViewMode}
                            >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Account Information</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Username <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="userName"
                                value={formData.userName}
                                onChange={handleChange}
                                placeholder="Enter username"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Role <span className="required-star">*</span></label>
                            <select
                                name="roleId"
                                value={formData.roleId}
                                onChange={handleChange}
                                disabled={isViewMode}
                            >
                                <option value="">Select Role</option>
                                {roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.roleName.replace("_", " ")}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/employees')} className="cancel-btn">
                        {isViewMode ? 'Back to List' : 'Cancel'}
                    </button>
                    {!isViewMode && (
                        <button type="submit" className="save-btn" disabled={saving}>
                            {saving ? 'Updating...' : <><MdSave /> Update Employee</>}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditEmployee;
